import React, { useState } from "react";
import { IFullPlanDetail, IFullPlanDetailPPO } from "../../../interfaces/api";
import {
  getDoubleDataPPO,
  getDoubleDeepHMO,
  getDoubleDeepPPO,
  getSingleDeepHMO,
  getTierHMO,
  getTierPPOO,
} from "../../../functions/planDetails";
import SingleInfoBenefitTripled from "./SingleInfoBenefitTripled.component";
import PPODoubleInfoBenefitTripled from "./PPODoubleInfoBenefitTripled.component";
import comArray from "../../../shared/CompareData";
import DoubleInfoBenefitTripled from "./DoubleInfoBenefitTripled.component";
import comFullArrayEquality from "../../../shared/EqualityArray.component";
import PPODoubleDeepInfoBenefitTripled from "./PPODoubleDeepBenefitTripled.component";
import { useZipRootLoaderData } from "../../../hooks/useZipRootLoaderData";

interface IProps {
  compData: IFullPlanDetail[] | IFullPlanDetailPPO[];
  highlightDifferences: boolean;
  uniqueName: string;
}

const ComparisonTablePrescriptions = (props: IProps) => {
  const { selectedYear } = useZipRootLoaderData();
  const [isRetail, setIsNotRetail] = useState<boolean>(true);
  const plansNumber = props.compData.length;
  const equality = comFullArrayEquality(props.compData);

  const prescEqualityData = equality.find(
    (item) => item.name === props.uniqueName
  );
  const tiersEqualityData = equality.find(
    (item) => item.name === "prescriptions-tiers"
  );
  const isPartDEqual = prescEqualityData?.childrens.find(
    (item) => item.name === "Part D Deductible"
  )?.equal;
  const isInitialCoverageEqual = prescEqualityData?.childrens.find(
    (item) => item.name === "Initial Coverage Limit"
  )?.equal;
  const isPartDOutEqual = prescEqualityData?.childrens.find(
    (item) => item.name === "Part D Out-of-Pocket Threshold"
  )?.equal;
  const isTier1 = tiersEqualityData?.childrens.find(
    (item) => item.name === "Tier 1: Preferred Generic"
  )?.equal;
  const isTier2 = tiersEqualityData?.childrens.find(
    (item) => item.name === "Tier 2: Generic"
  )?.equal;
  const isTier3 = tiersEqualityData?.childrens.find(
    (item) => item.name === "Tier 3: Preferred Brand"
  )?.equal;
  const isTier4 = tiersEqualityData?.childrens.find(
    (item) => item.name === "Tier 4: Non-Preferred"
  )?.equal;
  const isTier5 = tiersEqualityData?.childrens.find(
    (item) => item.name === "Tier 5: Specialty Tier"
  )?.equal;
  const isTier6 = tiersEqualityData?.childrens.find(
    (item) => item.name === "Tier 6: Select Care"
  )?.equal;

  const retailUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsNotRetail(true);
  };
  const retailDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsNotRetail(false);
  };

  return (
    <div
      className="grid-x"
      style={{
        backgroundColor: "white",
        border: "1px solid #D3E1F0",
      }}
    >
      {comArray.map((segment) =>
        segment.name === props.uniqueName
          ? segment.childrens.map((part, index) => (
              <div
                className="grid-x small-12"
                key={index}
                style={{ fontSize: "18px" }}
              >
                <div
                  className={
                    plansNumber === 3 ? "cell small-3" : "cell small-4"
                  }
                  style={{
                    height: "100%",
                    padding: "32px 20px",
                    borderBottom: "1px solid #D3E1F0",
                    textAlign: "start",
                    fontWeight: 600,
                  }}
                >
                  {part.name}
                </div>
                {props.compData.map((plan, index) => (
                  <div
                    className={
                      plansNumber === 3 ? "cell small-3" : "cell small-4"
                    }
                    key={index}
                  >
                    {part.name === "Part D Deductible" ? (
                      plan.PlanDetails[0].company[0].healthPlan[0].lob ===
                      "HMO" ? (
                        <SingleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isPartDEqual}
                          benefit={getDoubleDeepHMO(
                            plan as IFullPlanDetail,
                            "Part D Deductible",
                            selectedYear === 2024
                              ? "Part D Deductible In Network Standard Medicare-defined"
                              : "Part D Deductible in network standard medicare-defined"
                          )}
                        />
                      ) : (
                        <PPODoubleDeepInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isPartDEqual}
                          benefit={getDoubleDeepPPO(
                            plan as IFullPlanDetailPPO,
                            "Part D Deductible",
                            selectedYear === 2024
                              ? "Part D Deductible In Network Standard Medicare-defined"
                              : "Part D Deductible in network standard medicare-defined"
                          )}
                        />
                      )
                    ) : null}
                    {part.name === "Initial Coverage Limit" ? (
                      plan.PlanDetails[0].company[0].healthPlan[0].lob ===
                      "HMO" ? (
                        <SingleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isInitialCoverageEqual}
                          benefit={getSingleDeepHMO(
                            plan as IFullPlanDetail,
                            "Initial Coverage Limit"
                          )}
                        />
                      ) : (
                        <PPODoubleDeepInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isInitialCoverageEqual}
                          benefit={getDoubleDeepPPO(
                            plan as IFullPlanDetailPPO,
                            "Initial Coverage Limit",
                            "Initial Coverage Limit"
                          )}
                        />
                      )
                    ) : null}
                    {part.name === "Part D Out-of-Pocket Threshold" ? (
                      plan.PlanDetails[0].company[0].healthPlan[0].lob ===
                      "HMO" ? (
                        <SingleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isPartDOutEqual}
                          benefit={getSingleDeepHMO(
                            plan as IFullPlanDetail,
                            "Part D Out of Pocket Threshold"
                          )}
                        />
                      ) : (
                        <PPODoubleDeepInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isInitialCoverageEqual}
                          benefit={getDoubleDeepPPO(
                            plan as IFullPlanDetailPPO,
                            "Part D Out of Pocket Threshold",
                            selectedYear === 2024
                              ? "Part D Out Of Pocket Threshold"
                              : "Part D Out of Pocket Threshold"
                          )}
                        />
                      )
                    ) : null}
                  </div>
                ))}
              </div>
            ))
          : null
      )}
      <div
        className="grid-x"
        style={{
          backgroundColor: "#F5F9FD",
          width: "100%",
          padding: "32px 20px",
          borderBottom: "1px solid #D3E1F0",
          justifyContent: "space-between",
        }}
      >
        <div
          className="cell small-4"
          style={{
            textAlign: "start",
            fontSize: "25px",
            fontWeight: 600,
          }}
        >
          Tiers
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            fontSize: 18,
            fontWeight: 600,
          }}
        >
          <button
            type="button"
            style={{
              paddingRight: 16,
              color: isRetail ? "#8A8A8A" : "#1B54C2",
              cursor: isRetail ? "not-allowed" : "pointer",
            }}
            onClick={retailUp}
          >
            Retail Standard
          </button>
          <div style={{ color: "#8A8A8A", fontSize: 20 }} aria-hidden="true">
            |
          </div>
          <button
            type="button"
            style={{
              paddingLeft: 16,
              color: !isRetail ? "#8A8A8A" : "#1B54C2",
              cursor: !isRetail ? "not-allowed" : "pointer",
            }}
            onClick={retailDown}
          >
            Mail Order Standard
          </button>
        </div>
      </div>
      {comArray.map((segment) =>
        segment.name === "prescriptions-tiers"
          ? segment.childrens.map((part, index) => (
              <div key={index} className="grid-x small-12">
                <div
                  className={
                    plansNumber === 3 ? "cell small-3" : "cell small-4"
                  }
                  style={{
                    height: "100%",
                    padding: "32px 20px",
                    borderBottom: "1px solid #D3E1F0",
                    textAlign: "start",
                    fontSize: "18px",
                    fontWeight: 600,
                  }}
                >
                  {part.name}
                </div>
                {props.compData.map((plan, index) => (
                  <div
                    className={
                      plansNumber === 3 ? "cell small-3" : "cell small-4"
                    }
                    key={index}
                  >
                    {part.name === "Tier 1: Preferred Generic" ? (
                      plan.PlanDetails[0].company[0].healthPlan[0].lob ===
                      "HMO" ? (
                        <DoubleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isTier1}
                          benefit={getTierHMO(
                            plan as IFullPlanDetail,
                            "Preferred Generic Drugs"
                          )}
                          isRetail={isRetail}
                        />
                      ) : (
                        <DoubleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isTier1}
                          benefit={getTierPPOO(
                            plan as IFullPlanDetailPPO,
                            "Preferred Generic Drugs"
                          )}
                          isRetail={false}
                        />
                      )
                    ) : null}
                    {part.name === "Tier 2: Generic" ? (
                      plan.PlanDetails[0].company[0].healthPlan[0].lob ===
                      "HMO" ? (
                        <DoubleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isTier2}
                          benefit={getTierHMO(
                            plan as IFullPlanDetail,
                            "Generic Drugs"
                          )}
                          isRetail={isRetail}
                        />
                      ) : (
                        <DoubleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isTier1}
                          benefit={getTierPPOO(
                            plan as IFullPlanDetailPPO,
                            "Generic Drugs"
                          )}
                          isRetail={false}
                        />
                      )
                    ) : null}
                    {part.name === "Tier 3: Preferred Brand" ? (
                      plan.PlanDetails[0].company[0].healthPlan[0].lob ===
                      "HMO" ? (
                        <DoubleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isTier3}
                          benefit={getTierHMO(
                            plan as IFullPlanDetail,
                            "Preferred Brand Drugs"
                          )}
                          isRetail={isRetail}
                        />
                      ) : (
                        <DoubleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isTier1}
                          benefit={getTierPPOO(
                            plan as IFullPlanDetailPPO,
                            "Preferred Brand Drugs"
                          )}
                          isRetail={false}
                        />
                      )
                    ) : null}
                    {part.name === "Tier 4: Non-Preferred" ? (
                      plan.PlanDetails[0].company[0].healthPlan[0].lob ===
                      "HMO" ? (
                        <DoubleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isTier4}
                          benefit={getTierHMO(
                            plan as IFullPlanDetail,
                            selectedYear === 2024
                              ? "Non-Preferred Drugs"
                              : "Non-Preferred Brand Drugs"
                          )}
                          isRetail={isRetail}
                        />
                      ) : (
                        <DoubleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isTier1}
                          benefit={getTierPPOO(
                            plan as IFullPlanDetailPPO,
                            selectedYear === 2024
                              ? "Non-Preferred Drugs"
                              : "Non-Preferred Brand Drugs"
                          )}
                          isRetail={false}
                        />
                      )
                    ) : null}
                    {part.name === "Tier 5: Specialty Tier" ? (
                      plan.PlanDetails[0].company[0].healthPlan[0].lob ===
                      "HMO" ? (
                        <DoubleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isTier5}
                          benefit={getTierHMO(
                            plan as IFullPlanDetail,
                            "Specialty Tier Drugs"
                          )}
                          isRetail={isRetail}
                        />
                      ) : (
                        <DoubleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isTier1}
                          benefit={getTierPPOO(
                            plan as IFullPlanDetailPPO,
                            "Specialty Tier Drugs"
                          )}
                          isRetail={false}
                        />
                      )
                    ) : null}
                    {part.name === "Tier 6: Select Care" ? (
                      plan.PlanDetails[0].company[0].healthPlan[0].lob ===
                      "HMO" ? (
                        <DoubleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isTier6}
                          benefit={getTierHMO(
                            plan as IFullPlanDetail,
                            "Select Care Drugs"
                          )}
                          isRetail={isRetail}
                        />
                      ) : (
                        <DoubleInfoBenefitTripled
                          differences={props.highlightDifferences}
                          equal={isTier1}
                          benefit={getTierPPOO(
                            plan as IFullPlanDetailPPO,
                            "Select Care Drugs"
                          )}
                          isRetail={false}
                        />
                      )
                    ) : null}
                  </div>
                ))}
              </div>
            ))
          : null
      )}
    </div>
  );
};

export default ComparisonTablePrescriptions;
